/**
 * ============================================================
 * KEJJA OS — src/components/admin/UserDetailDrawer.tsx
 * Side drawer — full user profile + homes + actions.
 * ============================================================
 */

import { useEffect } from 'react';
import {
  X, Shield, ShieldOff, UserX, UserCheck, LogOut, Home as HomeIcon,
  Mail, Phone, Globe, Clock, Loader2,
} from 'lucide-react';
import {
  useAdminUserDetail, useSetSuperAdmin, useDeactivateUser,
  useReactivateUser, useRevokeSessions,
} from '@/hooks/useAdmin';
import { cn } from '@/lib/utils';

export default function UserDetailDrawer({ userId, onClose }: { userId: string | null; onClose: () => void }) {
  const { data: u, isLoading } = useAdminUserDetail(userId);
  const setSuperMut = useSetSuperAdmin();
  const deactivateMut = useDeactivateUser();
  const reactivateMut = useReactivateUser();
  const revokeMut = useRevokeSessions();

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  if (!userId) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-slate-950 border-l border-slate-800 z-50 overflow-y-auto text-slate-100 shadow-2xl">

        <div className="sticky top-0 bg-slate-950/90 backdrop-blur border-b border-slate-800 px-5 py-3 flex items-center justify-between z-10">
          <div className="text-sm font-semibold">User detail</div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {isLoading || !u ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="p-5 space-y-5">

            {/* Identity block */}
            <div>
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-full bg-slate-800 text-slate-300 flex items-center justify-center flex-shrink-0 font-semibold">
                  {(u.displayName || u.email).slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h2 className="text-base font-bold truncate">{u.displayName}</h2>
                    {u.isSuperAdmin && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-400 font-semibold uppercase">Super admin</span>}
                  </div>
                  <div className="text-xs text-slate-400 truncate">{u.email}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                <StatusBadge active={u.isActive} verified={u.emailVerified} deleted={!!u.deletedAt} />
              </div>
            </div>

            {/* Properties */}
            <div className="bg-slate-900 border border-slate-800 rounded-lg divide-y divide-slate-800">
              <Field icon={Mail} label="Email">{u.email}</Field>
              {u.phoneNumber && <Field icon={Phone} label="Phone">{u.phoneNumber}</Field>}
              <Field icon={Globe} label="Timezone">{u.timezone}</Field>
              <Field icon={Clock} label="Joined">{formatDate(u.createdAt)}</Field>
              <Field icon={Clock} label="Last login">{u.lastLoginAt ? formatDate(u.lastLoginAt) : '—'}</Field>
              <Field icon={Clock} label="Onboarded">{u.onboardedAt ? formatDate(u.onboardedAt) : 'Not yet'}</Field>
            </div>

            {/* Activity snapshot */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Activity</div>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Notifs 30d" value={u.activity.notifications30d} />
                <Metric label="Push subs"  value={u.activity.pushSubscriptions} />
                <Metric label="Sessions"   value={u.activity.adminSessions} />
              </div>
            </div>

            {/* Homes */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Homes ({u.homes.length})</div>
              {u.homes.length === 0 ? (
                <div className="text-xs text-slate-500 bg-slate-900 rounded-md p-3 border border-slate-800">Not a member of any home yet.</div>
              ) : (
                <div className="bg-slate-900 border border-slate-800 rounded-lg divide-y divide-slate-800">
                  {u.homes.map((h) => (
                    <div key={h.homeId} className="p-3 flex items-center gap-3">
                      <div className="h-7 w-7 rounded bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
                        <HomeIcon className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{h.homeName}</div>
                        <div className="text-[11px] text-slate-400">Joined {formatDate(h.joinedAt)}</div>
                      </div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 font-medium uppercase">{h.role}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Actions</div>
              <div className="space-y-2">
                <ActionButton
                  icon={u.isSuperAdmin ? ShieldOff : Shield}
                  label={u.isSuperAdmin ? 'Remove super admin' : 'Grant super admin'}
                  tone="rose"
                  onClick={() => {
                    if (confirm(u.isSuperAdmin
                      ? `Remove super admin rights from ${u.email}?`
                      : `Grant super admin rights to ${u.email}? They will have full system access.`)) {
                      setSuperMut.mutate({ userId: u.id, value: !u.isSuperAdmin });
                    }
                  }}
                />
                {u.isActive ? (
                  <ActionButton
                    icon={UserX} label="Deactivate account" tone="amber"
                    onClick={() => {
                      if (confirm(`Deactivate ${u.email}? They won't be able to log in.`)) {
                        deactivateMut.mutate(u.id);
                      }
                    }}
                  />
                ) : (
                  <ActionButton
                    icon={UserCheck} label="Reactivate account" tone="emerald"
                    onClick={() => reactivateMut.mutate(u.id)}
                  />
                )}
                {u.activity.adminSessions > 0 && (
                  <ActionButton
                    icon={LogOut} label={`Revoke all sessions (${u.activity.adminSessions})`} tone="slate"
                    onClick={() => {
                      if (confirm(`Revoke all ${u.activity.adminSessions} admin session(s) for ${u.email}? They will be signed out.`)) {
                        revokeMut.mutate(u.id);
                      }
                    }}
                  />
                )}
              </div>
            </div>

            <div className="text-[10px] text-slate-600 text-center pt-2 font-mono">
              {u.id}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Field({ icon: Icon, label, children }: { icon: React.ComponentType<{ className?: string }>; label: string; children: React.ReactNode }) {
  return (
    <div className="p-3 flex items-center gap-3">
      <Icon className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold w-20">{label}</div>
      <div className="text-xs text-slate-200 flex-1 truncate text-right">{children}</div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-md p-2.5">
      <div className="text-lg font-bold tabular-nums">{value.toLocaleString()}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-widest">{label}</div>
    </div>
  );
}

function StatusBadge({ active, verified, deleted }: { active: boolean; verified: boolean; deleted: boolean }) {
  const items: Array<{ text: string; cls: string }> = [];
  if (deleted) items.push({ text: 'deleted', cls: 'bg-rose-500/15 text-rose-400' });
  else if (!active) items.push({ text: 'inactive', cls: 'bg-amber-500/15 text-amber-400' });
  else items.push({ text: 'active', cls: 'bg-emerald-500/15 text-emerald-400' });
  items.push(verified ? { text: 'email verified', cls: 'bg-slate-800 text-slate-300' } : { text: 'unverified', cls: 'bg-slate-800 text-slate-400' });
  return <>{items.map((i, idx) => (
    <span key={idx} className={cn('px-1.5 py-0.5 rounded font-medium', i.cls)}>{i.text}</span>
  ))}</>;
}

function ActionButton({ icon: Icon, label, tone, onClick }: {
  icon: React.ComponentType<{ className?: string }>; label: string;
  tone: 'rose' | 'amber' | 'emerald' | 'slate'; onClick: () => void;
}) {
  const cls = {
    rose:    'text-rose-300    hover:bg-rose-950/30    border-rose-900/50',
    amber:   'text-amber-300   hover:bg-amber-950/30   border-amber-900/50',
    emerald: 'text-emerald-300 hover:bg-emerald-950/30 border-emerald-900/50',
    slate:   'text-slate-300   hover:bg-slate-800      border-slate-700',
  }[tone];
  return (
    <button onClick={onClick}
      className={cn('w-full flex items-center gap-2 px-3 py-2.5 rounded-md border bg-slate-900 text-sm transition', cls)}>
      <Icon className="w-4 h-4" />
      <span className="flex-1 text-left">{label}</span>
    </button>
  );
}

function formatDate(iso: string | Date): string {
  return new Date(iso).toLocaleString('en-KE', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}
